/**
 * ContactShadowsNode — TSL screen-space contact shadows.
 *
 * Algorithm (GDC / Bungie / UE5 technique):
 *
 *   For each pixel P:
 *   1. Reconstruct view-space position from depth buffer.
 *   2. Apply a surface slope bias along the view-space light direction to
 *      prevent self-shadowing acne on the receiver.
 *   3. Optionally jitter the start offset using interleaved gradient noise
 *      (IGN) — hides banding and amortises over temporal AA frames.
 *   4. March N steps along the view-space light direction, advancing by
 *      stepSize each iteration (stepSize = maxDistance / sampleCount).
 *   5. At each step:
 *       a. Project the current march position back to screen UV.
 *       b. Read the hardware depth at that UV.
 *       c. Reconstruct the view-space depth of the scene sample.
 *       d. If marchDepth > sceneDepth + thickness → in shadow → early exit.
 *   6. The result is a scalar in [0, 1]:
 *       0 = fully lit, 1 = fully shadowed.
 *      It is returned as outputNode = sceneColor * (1 - shadow * strength).
 *
 * Position reconstruction (depth → view-space):
 *   ndc.x  = uv.x * 2 - 1
 *   ndc.y  = 1 - uv.y * 2      (THE POST-UV LAW: a post quad's uv starts at the TOP)
 *   ndc.z  = depth * 2 - 1   (WebGL NDC range [-1,1]; WebGPU is [0,1])
 *   clip   = vec4(ndc, 1)
 *   view   = invProjection * clip
 *   view  /= view.w            (perspective divide)
 *
 * The pass uses THREE uniforms so light direction and all config values
 * can be updated each frame without rebuilding the node graph.
 *
 * Output:
 *   contactShadow(colorNode, depthNode, camera, config) → TSL node
 *   The returned node is the shadowed scene colour, ready to be fed
 *   to the next pass in the PostProcessingPipeline chain.
 */

import * as THREE from 'three/webgpu';
import {
  Fn,
  Loop,
  If,
  uniform,
  float,
  int,
  vec2,
  vec3,
  vec4,
  uv,
  clamp,
  mix,
  select,
  smoothstep,
  perspectiveDepthToViewZ,
  textureSize,
  abs,
  cross,
  Break,
  screenCoordinate,
} from 'three/tsl';
import type { ContactShadowsConfig } from './ContactShadowsConfig.js';
import { resolveContactShadowsConfig } from './ContactShadowsConfig.js';
import { depthBeyond } from '../depth/skyMask.js';

// ─── Public types ────────────────────────────────────────────────────────────

/** Live uniforms exposed so callers can update them each frame. */
export interface ContactShadowsUniforms {
  /** Normalised view-space light direction (updated each frame from scene). */
  lightDirView: { value: THREE.Vector3 };
  /** Shadow strength 0–1 (from config, mutable at runtime). */
  strength: { value: number };
  /** Depth-comparison thickness in view-space metres. */
  thickness: { value: number };
  /** 1 / projectionMatrix.elements[0] — must update on FOV/resize. */
  invM11: { value: number };
  /** 1 / projectionMatrix.elements[5]. */
  invM22: { value: number };
  /** projectionMatrix.elements[0] (forward-projection x scale). */
  m11: { value: number };
  /** projectionMatrix.elements[5] (forward-projection y scale). */
  m22: { value: number };
  /** projectionMatrix.elements[10]. */
  m33: { value: number };
  /** projectionMatrix.elements[14]. */
  m43: { value: number };
  /** camera.near — drives the coordinate-system-aware depth linearisation. */
  near: { value: number };
  /** camera.far. */
  far: { value: number };
  /** 0..1 — the key light's reach: 0 below the horizon or with the light off, 1 once it is ~4° up
   *  (`contactShadowLightFade`). Refreshed each frame beside `lightDirView`. */
  lightFade: { value: number };
}

/** Return value of createContactShadowsNode. */
export interface ContactShadowsNodeResult {
  /**
   * TSL output node — the scene colour darkened by contact shadows.
   * Feed this as outputNode into the PostProcessingPipeline chain in place
   * of the raw scene colour node.
   */
  node: unknown;
  /** Live uniforms — update lightDirView every frame from the active DirectionalLight. */
  uniforms: ContactShadowsUniforms;
  /** Resolved configuration (defaults + user overrides). */
  config: ContactShadowsConfig;
}

// ─── Interleaved Gradient Noise (Jimenez, 2014) ──────────────────────────────
//
// IGN produces a deterministic, spatially-uniform noise pattern in [0,1].
// It is computed from integer pixel coordinates and has very good spectral
// properties for temporal AA jitter amortisation.
//
//   ign(x, y) = fract(52.9829189 * fract(0.06711056 * x + 0.00583715 * y))
//
// We replicate this in TSL using float arithmetic (pixel coords via uv * resolution).
// The exact integer pixel coordinate is unavailable in pure TSL post-passes, so we
// approximate with screenUV * a large constant — giving good pseudo-random offset.

// ─── Depth reconstruction helpers ───────────────────────────────────────────
//
// Given:
//   uv      : screen UV in [0,1]
//   rawDepth: depth value from the depth texture (WebGL: [0,1] hardware depth)
//   invProj : camera inverse projection matrix
//
// NDC position (WebGL convention, z in [-1,1]):
//   ndc = (uv * 2 - 1, rawDepth * 2 - 1)
//
// Clip-space position (w=1 before perspective divide):
//   clip = invProj * vec4(ndc, 1.0)
//   viewPos = clip.xyz / clip.w
//
// viewPos.z is NEGATIVE in Three.js view space (right-handed, camera looks -Z).
// Depth increases as abs(viewPos.z) increases.
// The depth buffer stores projected Z, which is monotonically related to -viewPos.z.
// We compare marchPos.z against scenePos.z: both are view-space z (negative).
// marchPos is DEEPER (further from camera) when marchPos.z < scenePos.z.

// ─── createContactShadowsNode ────────────────────────────────────────────────

/**
 * Creates the screen-space contact shadows TSL node.
 *
 * @param colorNode  Scene colour texture node from `scenePass.getTextureNode('output')`.
 * @param depthNode  Scene depth texture node from `scenePass.getTextureNode('depth')`.
 * @param camera     The active camera (used for inverse projection).
 * @param partial    Optional config overrides.
 * @param normalNode Optional scene-pass VIEW-space normal (the MRT `normal` target, `normalView` raw): when given, the
 *                   shadow is gated by N·L (`contactFaceGate`) — 0 on a face turned from the light. Without one the
 *                   pass derives the face normal from `depthNode` (`contactNormalFromDepth`, 8 more depth taps) and gates the same.
 */
export function createContactShadowsNode(
  colorNode: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  partial?: Partial<ContactShadowsConfig>,
  normalNode?: unknown,
): ContactShadowsNodeResult {
  const cfg = resolveContactShadowsConfig(partial);

  // ── Live uniforms ──────────────────────────────────────────────────────────
  // Initialise light direction pointing TOWARD the light (straight up for an
  // overhead sun) — the march below advances along lightDirView toward the
  // occluders that sit between the receiver and the light. (Updated each frame.)
  const uLightDirView = uniform(new THREE.Vector3(0, 1, 0));
  const uStrength     = uniform(cfg.strength);
  const uThickness    = uniform(cfg.thickness);
  // Projection-matrix coefficients used for view-space depth reconstruction.
  // These MUST be live uniforms (not baked float() constants) — otherwise
  // any camera resize, FOV change, or projection update silently breaks
  // contact shadows because the shader keeps reconstructing depth using
  // the matrix that was active at node-construction time.
  const _initPm = (camera as THREE.PerspectiveCamera).projectionMatrix.elements;
  const uInvM11 = uniform(1.0 / (_initPm[0] === 0 ? 1e-6 : _initPm[0]));
  const uInvM22 = uniform(1.0 / (_initPm[5] === 0 ? 1e-6 : _initPm[5]));
  const uM11    = uniform(_initPm[0]);
  const uM22    = uniform(_initPm[5]);
  const uM33    = uniform(_initPm[10]);
  const uM43    = uniform(_initPm[14]);
  const _camNF = camera as { near?: number; far?: number };
  const uNear   = uniform(typeof _camNF.near === 'number' ? _camNF.near : 0.1);
  const uFar    = uniform(typeof _camNF.far === 'number' ? _camNF.far : 1000);
  const uLightFade = uniform(1);

  // Baked constants (not uniforms — baked into shader at compile time for perf).
  const stepSize = cfg.maxDistance / cfg.sampleCount;       // metres per step

  // We need the camera inverse projection matrix as a uniform so the shader can
  // reconstruct view-space positions from depth + UV.
  // THREE.js provides cameraProjectionMatrixInverse as a built-in TSL node.
  // We import it from three/tsl — it auto-updates with the camera each frame.

  // ── TSL kernel ─────────────────────────────────────────────────────────────
  const contactShadowFn = Fn((): any => {
    // ── 1. Current pixel UV ──────────────────────────────────────────────────
    const pixelUV = uv().toVar();

    // ── 2. Sample depth at the receiver pixel ────────────────────────────────
    // depthNode is the scene depth texture from the pass node.
    // We sample it at the current pixel's UV.
    const rawDepth = ((depthNode as any).sample
      ? (depthNode as any).sample(pixelUV)
      : (depthNode as any).uv
        ? (depthNode as any).uv(pixelUV)
        : (depthNode as any)
    ).toVar();

    // Skip far pixels: return full-lit (shadow = 0), applied as the lerp
    // shadow = shadow * (1 - isSky).
    //
    // NOTE (2026-09-14): 0.9999 on RAW depth is NOT the far plane — hardware
    // depth is non-linear, so with a 0.05 m near plane this is z ≈ 500 m (see
    // render/depth/skyMask.ts for the arithmetic and the three passes where
    // the same assumption WAS a bug). Here it is harmless and deliberate:
    // contact shadows are a short-range grounding effect, so a 500 m cutoff
    // costs nothing visually and skips the raymarch on distant terrain.
    // Do NOT "fix" this to skyMask() — that would re-enable the march for
    // every far pixel for no visible gain.
    // (reversed-Z, 2026-09-29: the same ~500 m cutoff under either depth convention — depthBeyond mirrors the literal)
    const isSky = depthBeyond(rawDepth as any, 0.9999).greaterThan(float(0.5)).toVar();

    // ── 3. Reconstruct view-space position of the receiver ───────────────────
    // NDC xy from UV. THE POST-UV LAW (fix 2026-09-27): three's post-quad uv() is TOP-origin on both
    // backends (its own getViewPosition takes screenPosition.y.oneMinus()), so ndc.y = 1 − 2v. The old
    // `2v − 1` rebuilt every receiver at its vertical MIRROR (x, −y, z) and the reprojection below undid the
    // mirror on the way out, so the march ran along (Lx, −Ly, Lz): a light reflected through the view's
    // horizontal plane — a high sun marched INTO the ground and real contact shadows never formed.
    // CPU mirror: contactViewFromUv / contactUvFromView (pinned against three's projection by the tests).
    const ndcX = (pixelUV as any).x.mul(float(2)).sub(float(1)).toVar();
    const ndcY = float(1).sub((pixelUV as any).y.mul(float(2))).toVar();
    // NDC z (WebGL [-1,1] convention): rawDepth * 2 - 1
    const ndcZ = (rawDepth as any).mul(float(2)).sub(float(1)).toVar();

    // Reconstruct view-space position via inverse projection.
    // THREE.js TSL provides cameraProjectionMatrixInverse as a built-in node.
    // We import it dynamically to avoid a circular dependency at the type level.
    // Resolve at runtime via (THREE as any).
    // We use a manual reconstruction with the camera's projection matrix here,
    // feeding it as a uniform so we don't depend on TSL built-ins that may not
    // be available in all Three.js r182 builds.
    //
    // For a perspective camera (standard):
    //   projMatrix = camera.projectionMatrix
    //   invProj    = camera.projectionMatrixInverse
    //   clip = invProj * vec4(ndc, 1)
    //   view = clip.xyz / clip.w
    //
    // (An unused Matrix4 uniform used to be allocated here on every shader
    // build; the reconstruction below reads the scalar coefficient uniforms.)

    // Reconstruct:  clip = invProj * vec4(ndcX, ndcY, ndcZ, 1)
    // For a standard perspective matrix the formulas reduce to:
    //   viewX = ndcX * (-1 / proj[0])     (proj[0] = 2*near/(right-left))
    //   viewY = ndcY * (-1 / proj[5])     (proj[5] = 2*near/(top-bottom))
    //   viewZ = ... (not trivially separable, use full matrix)
    //
    // We perform the full 4x4 multiply via TSL vec4/mat4 ops.
    // Three/tsl exposes `mat4` and `vec4` — we compose them manually.
    //
    // Note: in practice the actual inverse-projection transform is done by
    // leveraging cameraProjectionMatrixInverse built-in, but since we can't
    // depend on TSL internal built-in availability across Three r182 patch
    // versions, we fall back to a uniform-based approach.
    //
    // Simplified: use the camera's projectionMatrixInverse to transform NDC → view.
    // The TSL code below performs: viewPos = invProj * vec4(ndcX, ndcY, ndcZ, 1)
    // and then perspective-divides.
    //
    // Because TSL does not expose matrix * vector natively in a simple portable
    // form without importing from addons, we use the algebraic expansion:
    //
    //   For a standard symmetric perspective proj matrix M:
    //     M^-1[0][0] = 1/M[0][0]      (x scale)
    //     M^-1[1][1] = 1/M[1][1]      (y scale)
    //   viewX = ndcX / projMatrix.elements[0]
    //   viewY = ndcY / projMatrix.elements[5]
    //   viewZ = -1 (in view space, at the near/far plane, z is reconstructed below)
    //
    // Full z reconstruction from NDC z for standard perspective:
    //   proj[10] = -(far+near)/(far-near),  proj[14] = -2*far*near/(far-near)
    //   viewZ    = proj[14] / (ndcZ + proj[10])     (rearranging standard formula)
    //
    // We extract these from the camera's projectionMatrix.elements.
    // Projection-matrix coefficients live in uniforms (declared above) so
    // they track camera resize / FOV updates each frame. ContactShadowsSystem
    // is responsible for refreshing them in execute().
    const invM11 = uInvM11;
    const invM22 = uInvM22;
    const m33    = uM33;
    const m43    = uM43;

    // View Z (negative in front of the camera). The old hand-rolled
    // `m43 / (ndcZ + m33)` was sign-inverted (three's inverse is −m43/(ndc+m33))
    // AND assumed WebGL's [-1,1] NDC-Z on a WebGPU-first engine — the march
    // clamped every step to z = −1e-6 and the pass never darkened a pixel.
    // perspectiveDepthToViewZ is coordinate-system- and reversed-Z-aware.
    void ndcZ;
    const viewZ = (perspectiveDepthToViewZ(rawDepth as any, uNear as any, uFar as any) as any).toVar();

    // View X, Y via linear scale: viewX = ndcX * viewZ * invM11 / (-1)
    // Derivation: x_ndc = x_view * proj[0] / (-viewZ)  → x_view = x_ndc * (-viewZ) / proj[0]
    const viewX = (ndcX as any).mul((viewZ as any).negate()).mul(invM11).toVar();
    const viewY = (ndcY as any).mul((viewZ as any).negate()).mul(invM22).toVar();

    // View-space receiver position
    const receiverPos = vec3(viewX, viewY, viewZ).toVar();

    // ── 4. Compute step vector in view space ─────────────────────────────────
    const stepSizeNode = float(stepSize);
    // Light direction in view space (uniform, updated each frame by ContactShadowsSystem).
    const lightDir = vec3(
      (uLightDirView as any).x,
      (uLightDirView as any).y,
      (uLightDirView as any).z,
    ).toVar();
    // Step: advance TOWARD the light source (reverse of shadow ray = toward light)
    // We ray-march from the receiver toward the light, checking if geometry occludes.
    const stepVec = (lightDir as any).mul(stepSizeNode).toVar();

    // ── 5. Jitter the start offset (interleaved gradient noise) ───────────────
    // IGN(x,y) = fract(52.9829189 * fract(0.06711056*x + 0.00583715*y))
    // x,y are pixel coordinates of the target this pass renders into.
    let startOffset = float(0).toVar();
    if (cfg.noise) {
      // WHY screenCoordinate (shader sweep 2026-10-06): IGN's low-discrepancy pattern needs unit pixel steps; uv × 1920/1080
      // stepped it by 3 per pixel on a 640×360 chain target (2 at 960×540, 1.5 at 1280×720) and the dither fell into a
      // structured lattice in every penumbra. The fragment's own pixel position is the coordinate the formula expects.
      const ux = (screenCoordinate as any).x;
      const uy = (screenCoordinate as any).y;
      const inner = (ux as any).mul(float(0.06711056)).add((uy as any).mul(float(0.00583715)));
      // fract via sub floor: x - floor(x)
      // TSL has .fract() as a method on float nodes
      const innerFract = (inner as any).fract ? (inner as any).fract() : inner;
      const outer = (innerFract as any).mul(float(52.9829189));
      const noise = (outer as any).fract ? (outer as any).fract() : outer;
      // Scale noise to [0, stepSize] so the jitter is sub-step
      startOffset = (noise as any).mul(stepSizeNode).toVar();
    }

    // Initial march position: receiver + slope bias + noise offset.
    // The slope bias (one step forward along the light) prevents the receiver
    // from self-shadowing due to limited depth precision at grazing angles.
    const slopeBiasNode = float(stepSize * cfg.slopeBias);
    const marchPos = ((receiverPos as any).add(
      (lightDir as any).mul((slopeBiasNode as any).add(startOffset)),
    )).toVar();

    // ── 6. Shadow accumulator ────────────────────────────────────────────────
    // 0.0 = lit, 1.0 = shadowed. We break on first hit (hard contact shadow).
    const shadowFactor = float(0.0).toVar();

    // Sample count as integer for Loop
    Loop(int(cfg.sampleCount), (): any => {
      // Advance march position along the light direction
      (marchPos as any).addAssign(stepVec);

      // ── Project marchPos to screen space ──────────────────────────────────
      // Forward projection (view → NDC):
      //   x_ndc = x_view * proj[0] / (-z_view)
      //   y_ndc = y_view * proj[5] / (-z_view)
      //   depth_ndc = (z_view * proj[10] + proj[14]) / (-z_view)
      const negZ = (marchPos as any).z.negate();
      // Guard against z = 0 (shouldn't happen for valid geometry, but defensive)
      const safeNegZ = (negZ as any).max(float(1e-6));
      // Forward-projection coefficients: same uniforms refreshed each frame
      // by ContactShadowsSystem (see uM11/uM22 declarations above).
      const m11 = uM11;
      const m22 = uM22;
      const marchNdcX = (marchPos as any).x.mul(m11).div(safeNegZ).toVar();
      const marchNdcY = (marchPos as any).y.mul(m22).div(safeNegZ).toVar();
      const marchNdcZ = ((marchPos as any).z.mul(m33).add(m43)).div(safeNegZ).toVar();

      // NDC → UV: u = ndc.x·0.5 + 0.5, v = 0.5 − ndc.y·0.5 (the inverse of step 3 — THE POST-UV LAW).
      const marchUV = vec2(
        (marchNdcX as any).mul(float(0.5)).add(float(0.5)),
        float(0.5).sub((marchNdcY as any).mul(float(0.5))),
      ).toVar();

      // Skip samples outside the screen
      const inScreen = (marchUV as any).x.greaterThanEqual(float(0.0))
        .and((marchUV as any).x.lessThanEqual(float(1.0)))
        .and((marchUV as any).y.greaterThanEqual(float(0.0)))
        .and((marchUV as any).y.lessThanEqual(float(1.0)));

      If(inScreen, (): any => {
        // Clamp UV for safe sampling
        const clampedUV = clamp(marchUV as any, float(0.0), float(1.0));

        // Sample scene depth at march UV
        const sampledDepth = ((depthNode as any).sample
          ? (depthNode as any).sample(clampedUV)
          : (depthNode as any).uv
            ? (depthNode as any).uv(clampedUV)
            : (depthNode as any)
        ).toVar();

        // Skip sky/background samples
        const isSkyStep = depthBeyond(sampledDepth as any, 0.9999).greaterThan(float(0.5));

        If((isSkyStep as any).not(), (): any => {
          // Reconstruct scene view-space Z at sampled position.
          // Convert hardware depth [0,1] → NDC z [-1,1] → view Z.
          const sceneViewZ = (perspectiveDepthToViewZ(sampledDepth as any, uNear as any, uFar as any) as any).toVar();

          // March view Z. WHY marchPos.z: the previous round-trip
          // `m43 / (marchNdcZ + m33)` was the NEGATED inverse of the forward
          // projection above (z = −m43/(ndc+m33)), so marchViewZ came out
          // POSITIVE while sceneViewZ is negative — zDiff was always < 0,
          // `isBehind` never fired and the pass darkened zero pixels (the
          // receiver-side fix at step 3 never reached the march). The march
          // position already IS view space; its z needs no reconstruction.
          void marchNdcZ;
          const marchViewZ = (marchPos as any).z.toVar();

          // Occlusion test:
          //   marchViewZ > sceneViewZ → march position is BEHIND scene geometry
          //   (more negative = further from camera in right-handed view space)
          //   In Three.js view space: z is negative (camera looks -Z).
          //   Closer to camera = larger z (less negative).
          //   Behind geometry means: marchViewZ < sceneViewZ (more negative).
          //
          //   Shadow condition: sceneViewZ - marchViewZ < thickness
          //     i.e., scene is between camera and march position by < thickness
          //   This avoids shadows leaking through thick geometry.
          const zDiff = (sceneViewZ as any).sub(marchViewZ).toVar();
          // In shadow if: zDiff > 0 (march is behind scene) AND zDiff < thickness
          const uThicknessLocal = uThickness;
          const isBehind = (zDiff as any).greaterThan(float(0.0));
          const isWithinThickness = (zDiff as any).lessThan(uThicknessLocal);

          If((isBehind as any).and(isWithinThickness), (): any => {
            // Fade the shadow based on how far along the march we are.
            // marchNdcZ in [-1,1]; convert to fraction of maxDistance [0,1].
            // Simple falloff: shadow is full at the receiver, fades to 0 at maxDistance.
            // We use the step count ratio rather than the z because step count is stable.
            // For now: hard shadow (factor = 1) — temporal AA will soften it.
            (shadowFactor as any).assign(float(1.0));
            // The first hit decides (shader sweep 2026-10-06): the header always said "break on first hit" and the loop
            // never did — every shadowed pixel kept marching (a depth sample a step) to set 1 again. Same result.
            Break();
          });
        });
      });
    });

    // ── 7. Sky mask: no shadow on background pixels ──────────────────────────
    // If receiver is sky (isSky), shadow = 0.
    const finalShadow = mix(shadowFactor as any, float(0.0), isSky as any).toVar();

    // ── 7b. The face gate (gaps wave 2026-09-28): a face turned from the light is entered by the march at once and
    // read ~1 % self-contact (6 % bands without the jitter) — and the key light adds nothing there anyway, so the pass
    // must not darken its ambient. N·L on the MRT's view normal (lightDirView is view space too); a zero normal (a
    // pixel nothing wrote) keeps the old behaviour. CPU mirror: contactFaceGate.
    // gaps wave round 2 (2026-09-28): WITHOUT an MRT normal (low / potato / minimal with the pass switched on, WebGL2
    // high where Atlas strips AO, a device whose attachment budget dropped the target) the gate ran nowhere and the
    // bands came back — so the pass derives the face normal from its own depth (contactNormalFromDepth, the CPU mirror).
    let nRaw: any = null;
    if (normalNode) {
      nRaw = (((normalNode as any).sample
        ? (normalNode as any).sample(pixelUV)
        : (normalNode as any).uv
          ? (normalNode as any).uv(pixelUV)
          : (normalNode as any)
      ) as any).xyz.toVar();
    } else if ((depthNode as any).sample || (depthNode as any).uv) {
      // Three's getNormalFromDepth scheme on this pass's own reconstruction (same uniforms, THE POST-UV LAW): per axis
      // the side whose LINEAR extrapolation (2·d1 − d2) predicts the centre best — hardware depth is affine in 1/z, so a
      // plane extrapolates exactly and the side across a silhouette or a crease loses. At the box's base that keeps the
      // ground pixel on the ground (a nearest-neighbour pick would take the face's equal depth at the crease).
      // The texel step is the DEPTH texture's own (under FSR it is coarser than the output this pass may run at).
      const sd = (o: any): any => ((depthNode as any).sample
        ? (depthNode as any).sample((pixelUV as any).add(o))
        : (depthNode as any).uv((pixelUV as any).add(o))) as any;
      const texel = vec2(float(1), float(1)).div(vec2(textureSize(depthNode as any, int(0)) as any)).toVar();
      const ox = vec2((texel as any).x, float(0)), oy = vec2(float(0), (texel as any).y);
      const l1 = sd((ox as any).negate()).x.toVar(), l2 = sd((ox as any).mul(-2)).x;
      const r1 = sd(ox).x.toVar(), r2 = sd((ox as any).mul(2)).x;
      const t1 = sd((oy as any).negate()).x.toVar(), t2 = sd((oy as any).mul(-2)).x;
      const b1 = sd(oy).x.toVar(), b2 = sd((oy as any).mul(2)).x;
      const c0 = (rawDepth as any).x;
      const errL = abs((l1 as any).mul(2).sub(l2).sub(c0)), errR = abs((r1 as any).mul(2).sub(r2).sub(c0));
      const errT = abs((t1 as any).mul(2).sub(t2).sub(c0)), errB = abs((b1 as any).mul(2).sub(b2).sub(c0));
      const viewAt = (o: any, d: any): any => {
        const q = (pixelUV as any).add(o);
        const z = (perspectiveDepthToViewZ(d, uNear as any, uFar as any) as any);
        return vec3(
          (q as any).x.mul(2).sub(1).mul((z as any).negate()).mul(invM11),
          float(1).sub((q as any).y.mul(2)).mul((z as any).negate()).mul(invM22),
          z,
        );
      };
      // Both candidates of each tangent point the same way: +x (screen right) and +y (screen UP — v grows downward).
      const tx = select((errL as any).lessThan(errR), (receiverPos as any).sub(viewAt((ox as any).negate(), l1)), viewAt(ox, r1).sub(receiverPos)).toVar();
      const ty = select((errB as any).lessThan(errT), (receiverPos as any).sub(viewAt(oy, b1)), viewAt((oy as any).negate(), t1).sub(receiverPos)).toVar();
      // Unit tangents, so |n| = sin of the angle between them: a degenerate pair falls under CONTACT_NORMAL_MIN_LEN (no gate).
      const txU = (tx as any).div((tx as any).length().max(float(1e-12)));
      const tyU = (ty as any).div((ty as any).length().max(float(1e-12)));
      nRaw = (cross(txU, tyU) as any).toVar(); // right × up = toward the camera (+z) on a surface facing it — normalView's sense
    }
    if (nRaw) {
      const nLen = (nRaw as any).length().toVar();
      const ndl = (nRaw as any).dot(lightDir).div((nLen as any).max(float(1e-4)));
      const faceGate = select((nLen as any).lessThan(float(CONTACT_NORMAL_MIN_LEN)), float(1),
        smoothstep(float(0), float(CONTACT_NORMAL_GATE_NDL), ndl as any));
      (finalShadow as any).mulAssign(faceGate);
    }

    // ── 8. Apply shadow to scene colour: darken by (1 - shadow * strength) ───
    // × the key light's fade: a contact shadow is the KEY light's — none once it is below the horizon or off.
    const darken = (finalShadow as any).mul(uStrength).mul(uLightFade).toVar();
    const shadowMultiplier = float(1.0).sub(darken as any).toVar();

    // Sample scene colour at current pixel UV
    const sceneColor = ((colorNode as any).sample
      ? (colorNode as any).sample(pixelUV)
      : (colorNode as any).uv
        ? (colorNode as any).uv(pixelUV)
        : (colorNode as any)
    ).toVar();

    // Multiply RGB by shadow factor, preserve alpha
    return vec4(
      (sceneColor as any).rgb.mul(shadowMultiplier as any),
      (sceneColor as any).a,
    );
  });

  const node = contactShadowFn();

  // ── Return ──────────────────────────────────────────────────────────────────
  const uniforms: ContactShadowsUniforms = {
    lightDirView: uLightDirView as any,
    strength:     uStrength as any,
    thickness:    uThickness as any,
    invM11:       uInvM11 as any,
    invM22:       uInvM22 as any,
    m11:          uM11 as any,
    m22:          uM22 as any,
    m33:          uM33 as any,
    m43:          uM43 as any,
    near:         uNear as any,
    far:          uFar as any,
    lightFade:    uLightFade as any,
  };

  return { node, uniforms, config: cfg };
}

// ─── CPU mirrors of the screen mapping (THE POST-UV LAW) ─────────────────────

/**
 * View-space position of a post-quad pixel — the CPU mirror of step 3 of the kernel.
 * `v` is the post quad's TOP-origin uv.y, so ndc.y = 1 − 2v; viewZ is negative in front of the camera.
 */
export function contactViewFromUv(
  u: number, v: number, viewZ: number, invM11: number, invM22: number,
): [number, number, number] {
  const ndcX = u * 2 - 1;
  const ndcY = 1 - v * 2;
  return [ndcX * -viewZ * invM11, ndcY * -viewZ * invM22, viewZ];
}

/** Top-origin post uv of a view-space point — the CPU mirror of the march's reprojection (inverse of the above). */
export function contactUvFromView(
  x: number, y: number, z: number, m11: number, m22: number,
): [number, number] {
  const negZ = Math.max(-z, 1e-6);
  return [(x * m11 / negZ) * 0.5 + 0.5, 0.5 - (y * m22 / negZ) * 0.5];
}

// ─── The key light ───────────────────────────────────────────────────────────

/** N·L at which the face gate reaches 1 (~6° off grazing): a contact shadow is the KEY light's, and a face turned from it
 *  (N·L ≤ 0) receives none of that light — the gate is 0 there, a smoothstep to 1 by this value. */
export const CONTACT_NORMAL_GATE_NDL = 0.1;
/** Below this length the MRT normal is treated as absent (a pixel nothing wrote): no gate. */
export const CONTACT_NORMAL_MIN_LEN = 1e-3;

/**
 * The face gate — the CPU mirror of the kernel's step 7b. `n` is the pixel's view-space normal as the MRT stores it
 * (need not be unit), `l` the view-space direction TOWARD the light (unit). 1 = the contact shadow applies in full.
 */
export function contactFaceGate(nx: number, ny: number, nz: number, lx: number, ly: number, lz: number): number {
  const len = Math.hypot(nx, ny, nz);
  if (!(len >= CONTACT_NORMAL_MIN_LEN)) return 1;
  const ndl = (nx * lx + ny * ly + nz * lz) / Math.max(len, 1e-4);
  const t = Math.min(1, Math.max(0, ndl / CONTACT_NORMAL_GATE_NDL));
  return t * t * (3 - 2 * t);
}

/** Hardware depth (standard, not reversed) → view-space z — three's `perspectiveDepthToViewZ`, the kernel's linearisation. */
export function contactViewZFromDepth(depth: number, near: number, far: number): number {
  return (near * far) / ((far - near) * depth - far);
}

/** The nine depth taps of the derived normal: the centre, and one / two texels left, right, up (t) and down (b). */
export interface ContactDepthTaps { c: number; l1: number; l2: number; r1: number; r2: number; t1: number; t2: number; b1: number; b2: number }

/**
 * The face normal the kernel derives from DEPTH when it has no MRT normal (step 7b) — its CPU mirror. `u, v` is the
 * receiver's top-origin post uv, `texelU / texelV` the depth texture's texel size. Per axis the side whose linear
 * extrapolation `2·d1 − d2` predicts the centre best supplies the tangent (hardware depth is affine in 1/z, so a plane
 * extrapolates exactly and a silhouette or crease loses). Returns `cross(unit +x tangent, unit +y tangent)` in view
 * space — toward the camera on a surface facing it; its length is the sine of the angle between the tangents (feed it
 * to `contactFaceGate`, which treats a degenerate one as "no gate").
 */
export function contactNormalFromDepth(
  d: ContactDepthTaps, u: number, v: number, texelU: number, texelV: number,
  near: number, far: number, invM11: number, invM22: number,
): [number, number, number] {
  const at = (du: number, dv: number, depth: number): [number, number, number] => {
    const z = contactViewZFromDepth(depth, near, far);
    return contactViewFromUv(u + du, v + dv, z, invM11, invM22);
  };
  const c = at(0, 0, d.c);
  const sub = (a: [number, number, number], b: [number, number, number]): [number, number, number] => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
  const errL = Math.abs(2 * d.l1 - d.l2 - d.c), errR = Math.abs(2 * d.r1 - d.r2 - d.c);
  const errT = Math.abs(2 * d.t1 - d.t2 - d.c), errB = Math.abs(2 * d.b1 - d.b2 - d.c);
  const tx = errL < errR ? sub(c, at(-texelU, 0, d.l1)) : sub(at(texelU, 0, d.r1), c);
  const ty = errB < errT ? sub(c, at(0, texelV, d.b1)) : sub(at(0, -texelV, d.t1), c);
  const nx = Math.max(Math.hypot(tx[0], tx[1], tx[2]), 1e-12), ny = Math.max(Math.hypot(ty[0], ty[1], ty[2]), 1e-12);
  const a = [tx[0] / nx, tx[1] / nx, tx[2] / nx], b = [ty[0] / ny, ty[1] / ny, ty[2] / ny];
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/** Sine of the elevation at which a contact shadow reaches full strength (~4°): the key light fades in from the horizon. */
export const CONTACT_FADE_ELEVATION_SIN = 0.07;

/**
 * The contact shadow's reach from its key light: 0 with the light off, 0 at or below the horizon, a
 * smoothstep to 1 at `CONTACT_FADE_ELEVATION_SIN`. `towardWorldY` is the WORLD-space direction TOWARD the
 * light, normalised. A sun below the horizon would otherwise march every receiver into the ground and a
 * dark-sky scene would keep its daytime contact lines (the pass multiplies the whole pixel, lamps included).
 */
export function contactShadowLightFade(towardWorldY: number, intensity: number): number {
  if (!(intensity > 0) || !Number.isFinite(towardWorldY)) return 0;
  const t = Math.min(1, Math.max(0, towardWorldY / CONTACT_FADE_ELEVATION_SIN));
  return t * t * (3 - 2 * t);
}

/** Scratch objects (zero-alloc per frame). */
const _lightDir   = new THREE.Vector3();
const _lightPos   = new THREE.Vector3();
const _targetPos  = new THREE.Vector3();
const _viewMatrix = new THREE.Matrix4();
/** Re-scan a scene's directional lights at least this often (calls): a light added after the first scan is found. */
const LIGHT_RESCAN_CALLS = 240;
// Per-scene directional-light list (see pickPrimaryDirectionalLight). WeakMap so a discarded scene never
// pins its lights.
const _dirLightCache = new WeakMap<THREE.Object3D, { lights: THREE.DirectionalLight[]; calls: number }>();

function attachedTo(obj: THREE.Object3D, root: THREE.Object3D): boolean {
  let p: THREE.Object3D | null = obj;
  while (p && p !== root) p = p.parent;
  return p === root;
}

function shownIn(obj: THREE.Object3D, root: THREE.Object3D): boolean {
  let p: THREE.Object3D | null = obj;
  while (p && p !== root) { if (!p.visible) return false; p = p.parent; }
  return p === root;
}

function directionalLightsOf(scene: THREE.Object3D): THREE.DirectionalLight[] {
  const c = _dirLightCache.get(scene);
  if (c && ++c.calls < LIGHT_RESCAN_CALLS && c.lights.every((l) => attachedTo(l, scene))) return c.lights;
  // traverse() visits EVERY node, so it runs once per LIGHT_RESCAN_CALLS (or when a cached light left).
  const lights: THREE.DirectionalLight[] = [];
  scene.traverse((obj) => { if (obj instanceof THREE.DirectionalLight) lights.push(obj); });
  _dirLightCache.set(scene, { lights, calls: 0 });
  return lights;
}

/**
 * The key light a contact shadow belongs to: the BRIGHTEST shown DirectionalLight (the first among equals),
 * else the first one found (its fade will be 0), else null. It used to be the FIRST DirectionalLight in the
 * traversal — the sun even at night with the moon up, or a personal world's parked daylight sun at 0 over
 * its preset's own key.
 */
export function pickPrimaryDirectionalLight(scene: THREE.Object3D): THREE.DirectionalLight | null {
  const lights = directionalLightsOf(scene);
  let best: THREE.DirectionalLight | null = null;
  let bestI = 0;
  for (const l of lights) {
    if (!shownIn(l, scene)) continue;
    if (l.intensity > bestI) { best = l; bestI = l.intensity; }
  }
  return best ?? lights[0] ?? null;
}

/** The contact shadow's light this frame: the view-space direction TOWARD it and its fade (0..1). */
export interface PrimaryLightState {
  /** Normalised, view space, toward the light. Shared scratch — copy it. */
  dirView: THREE.Vector3;
  /** `contactShadowLightFade` of the light (1 with no light — the pass keeps its old overhead default). */
  fade: number;
}

const _state: PrimaryLightState = { dirView: _lightDir, fade: 1 };

/**
 * Finds the contact shadow's key light (`pickPrimaryDirectionalLight`) and returns its direction in view
 * space, normalised, with its fade. With no DirectionalLight: straight up (0, +1, 0) in view space for an
 * identity camera, fade 1 (the pass's historic default).
 */
export function getPrimaryLightState(scene: THREE.Scene, camera: THREE.Camera): PrimaryLightState {
  const light = pickPrimaryDirectionalLight(scene);
  if (!light) {
    // The march expects a direction pointing TOWARD the light, so an overhead light → (0,+1,0).
    _lightDir.set(0, 1, 0);
    _state.fade = 1;
    return _state;
  }
  // Direction TOWARD the light (light − target), in WORLD space — what three lights with
  // (DirectionalLightNode reads both matrixWorlds). The march steps from the receiver TOWARD the light, so
  // it needs the toward-light direction, NOT `target − position` (the way the rays travel). Falls back to
  // the local positions when the world matrices have not been updated yet (a light never rendered).
  _lightPos.setFromMatrixPosition(light.matrixWorld);
  _targetPos.setFromMatrixPosition(light.target.matrixWorld);
  _lightDir.subVectors(_lightPos, _targetPos);
  if (_lightDir.lengthSq() < 1e-12) _lightDir.subVectors(light.position, light.target.position);
  if (_lightDir.lengthSq() < 1e-12) _lightDir.set(0, 1, 0);
  _lightDir.normalize();
  _state.fade = contactShadowLightFade(_lightDir.y, light.intensity);

  // Transform world → view space using camera's view matrix
  _viewMatrix.copy(camera.matrixWorldInverse);
  _lightDir.transformDirection(_viewMatrix);
  return _state;
}

/**
 * The key light's direction in view space, normalised (see `getPrimaryLightState`, which also gives the fade).
 *
 * Call this once per frame and assign the result to:
 *   contactShadowsUniforms.lightDirView.value.copy(dir)
 */
export function getPrimaryLightDirView(
  scene: THREE.Scene,
  camera: THREE.Camera,
): THREE.Vector3 {
  return getPrimaryLightState(scene, camera).dirView;
}
